import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import "./ImageEditor.scss";
import { fileUploads } from "../../../services/fileUploadService";
import Cropper from "react-easy-crop";
import CloseIcon from "@mui/icons-material/Close";
import Loader from "../../common/Loader";

const ImageEditorModal = ({
  imgSrc: initialImgSrc,
  onClose,
  onSave,
  onDelete,
  originalFile,
  aspectRatio = 1,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [currentImgSrc, setCurrentImgSrc] = useState(initialImgSrc);

  const initialCrop = useRef({ x: 0, y: 0 });
  const initialZoom = useRef(1);
  const initialRotation = useRef(0);

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.setAttribute("crossOrigin", "anonymous");
      image.src = url;
    });

  async function getCroppedImgHelper(imageSrc, pixelCrop, rotation = 0) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const rotRad = (rotation * Math.PI) / 180;
    const width = image.width;
    const height = image.height;

    const sin = Math.abs(Math.sin(rotRad));
    const cos = Math.abs(Math.cos(rotRad));
    const newWidth = cos * width + sin * height;
    const newHeight = sin * width + cos * height;

    canvas.width = newWidth;
    canvas.height = newHeight;

    ctx.translate(newWidth / 2, newHeight / 2);
    ctx.rotate(rotRad);
    ctx.translate(-width / 2, -height / 2);

    ctx.drawImage(image, 0, 0);

    const croppedCanvas = document.createElement("canvas");
    const croppedCtx = croppedCanvas.getContext("2d");

    croppedCanvas.width = pixelCrop.width;
    croppedCanvas.height = pixelCrop.height;

    croppedCtx.drawImage(
      canvas,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      croppedCanvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        "image/jpeg",
        0.95
      );
    });
  }

  useEffect(() => {
    setCurrentImgSrc(initialImgSrc);
  }, [initialImgSrc]);

  useEffect(() => {
    const isCropChanged =
      crop.x !== initialCrop.current.x || crop.y !== initialCrop.current.y;
    const isZoomChanged = zoom !== initialZoom.current;
    const isRotationChanged = rotation !== initialRotation.current;
    const isImageChanged = currentImgSrc !== initialImgSrc;

    setHasChanges(
      isCropChanged || isZoomChanged || isRotationChanged || isImageChanged
    );
  }, [crop.x, crop.y, zoom, rotation, currentImgSrc, initialImgSrc]);

  const handleChangePhoto = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const newImageUrl = URL.createObjectURL(file);
        setCurrentImgSrc(newImageUrl);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        setCroppedAreaPixels(null);
      }
    };
    input.click();
  };

  const getCroppedImg = async () => {
    if (!hasChanges) return originalFile;
    if (!croppedAreaPixels) return null;

    try {
      const blob = await getCroppedImgHelper(
        currentImgSrc,
        croppedAreaPixels,
        rotation
      );
      return blob;
    } catch (error) {
      console.error("Error cropping image:", error);
      return null;
    }
  };

  const handleApply = async () => {
    try {
      const result = await getCroppedImg();
      onSave(result);
      onClose();
    } catch (error) {
      console.error("Error applying changes:", error);
    }
  };

  return (
    <Modal open={!!currentImgSrc} onClose={onClose}>
      <Box className="modal-container">
        <div className="photo-header">
          <span className="photo-title">Profile photo</span>
          <div className="close-icon" onClick={onClose}>
            <CloseIcon />
          </div>
        </div>

        <div className="editor-container">
          <div className="crop-container">
            <Cropper
              image={currentImgSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={(_, croppedAreaPixels) => {
                setCroppedAreaPixels(croppedAreaPixels);
              }}
              classes={{
                containerClassName: "crop-container",
                mediaClassName: "crop-media",
                cropAreaClassName: "fixed-crop-area",
              }}
            />
          </div>

          <div className="image-controls">
            <div className="controls-wrapper">
              <div className="zoom-controls">
                <label className="zoom-label">Zoom</label>
                <div className="zoom-slider">
                  <button
                    className="zoom-btn"
                    onClick={() => setZoom((prev) => Math.max(prev - 0.1, 1))}
                  >
                    -
                  </button>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.1"
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                  />
                  <button
                    className="zoom-btn"
                    onClick={() => setZoom((prev) => Math.min(prev + 0.1, 3))}
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="rotate-controls">
                <button
                  onClick={() => setRotation((prev) => (prev - 90) % 360)}
                >
                  ↺
                </button>
                <button
                  onClick={() => setRotation((prev) => (prev + 90) % 360)}
                >
                  ↻
                </button>
              </div>
            </div>
          </div>
          <div className="action-controls">
            <div className="left-section">
              <button className="delete-btn" onClick={onDelete}>
                Delete photo
              </button>
            </div>
            <div className="right-section">
              <button className="change-btn" onClick={handleChangePhoto}>
                Change photo
              </button>
              <button className="apply-btn" onClick={handleApply}>
                Apply
              </button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

const ProfileImageEditor = forwardRef(
  ({ onImageUploaded, isLoading, aspectRatio = 1 }, ref) => {
    const [editingFile, setEditingFile] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [fileToUpload, setFileToUpload] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const fileInputRef = useRef(null);
    const MAX_FILE_SIZE = 78643200;

    const triggerFileSelect = () => {
      fileInputRef.current?.click();
    };

    useImperativeHandle(ref, () => ({
      triggerFileSelect,
    }));

    const handleFileSelect = (event) => {
      const file = event.target.files[0];
      if (!file) return;

      setErrorMsg("");

      if (file.size > MAX_FILE_SIZE) {
        setErrorMsg("The uploaded file is larger than 75 MB.");
        event.target.value = "";
        return;
      }

      if (!file.type.startsWith("image/")) {
        setErrorMsg("Only image files are allowed.");
        event.target.value = "";
        return;
      }

      setFileToUpload(file);
      setEditingFile(URL.createObjectURL(file));
      setEditModalOpen(true);
      event.target.value = "";
    };

    const handleEditedImage = async (result) => {
      try {
        setIsUploading(true);
        setErrorMsg("");
        let fileToProcess;

        if (result instanceof File) {
          fileToProcess = result;
        } else {
          const originalName = fileToUpload.name.split(".")[0];
          const sanitizedBaseName = originalName.replace(/\s+/g, "_");
          const originalExtension = fileToUpload.name
            .split(".")
            .pop()
            .toLowerCase();
          const finalExtension =
            originalExtension === "jpeg" ? "jpg" : originalExtension;

          fileToProcess = new File(
            [result],
            `${sanitizedBaseName}_edited_${Date.now()}.${finalExtension}`,
            {
              type: `image/${finalExtension === "png" ? "png" : "jpeg"}`,
              lastModified: Date.now(),
            }
          );
        }

        if (fileToProcess.size > MAX_FILE_SIZE) {
          setErrorMsg("The uploaded file is larger than 75 MB.");
          setIsUploading(false);
          return;
        }

        // Upload the file
        const uploadResponse = await fileUploads(fileToProcess);
        if (uploadResponse && uploadResponse[0]) {
          onImageUploaded(uploadResponse[0].fileUrl);
        }
      } catch (error) {
        console.error("Error processing image", error);
        setErrorMsg("Error uploading image. Please try again.");
      } finally {
        setIsUploading(false);
        setEditModalOpen(false);
        if (editingFile) URL.revokeObjectURL(editingFile);
        setFileToUpload(null);
        setEditingFile(null);
      }
    };

    return (
      <>
        {isUploading && <Loader />}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          style={{ display: "none" }}
          accept="image/jpeg, image/png, image/jpg"
        />
        {errorMsg && <div className="errorMsg">{errorMsg}</div>}
        {editModalOpen && (
          <ImageEditorModal
            imgSrc={editingFile}
            originalFile={fileToUpload}
            aspectRatio={aspectRatio}
            onSave={handleEditedImage}
            onClose={() => {
              setEditModalOpen(false);
              if (editingFile) URL.revokeObjectURL(editingFile);
              setFileToUpload(null);
              setEditingFile(null);
            }}
            onDelete={() => {
              setEditModalOpen(false);
              if (editingFile) URL.revokeObjectURL(editingFile);
              setFileToUpload(null);
              setEditingFile(null);
            }}
          />
        )}
      </>
    );
  }
);

ProfileImageEditor.displayName = "ProfileImageEditor";

export { ProfileImageEditor };
